import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
  LabelList,
  AreaChart,
  Area,
} from "recharts";
import {
  logCurrentUserInfo,
  initializeZohoSDK,
} from "../../api/CurrentUserData"; // Updated import
import { useEffect, useState } from "react";
import { getCARRecords } from "../../api/GETCAR"; // Import getCARRecords function

const dummyBarData = [
  { name: "Mon", value: 50 },
  { name: "Tue", value: 30 },
  { name: "Wed", value: 80 },
  { name: "Thu", value: 40 },
  { name: "Fri", value: 90 },
];

const dummyLineData = [
  { name: "Jan", value: 300 },
  { name: "Feb", value: 400 },
  { name: "Mar", value: 350 },
  { name: "Apr", value: 500 },
];

const dummyPieData = [
  { name: "Group A", value: 400 },
  { name: "Group B", value: 300 },
  { name: "Group C", value: 300 },
  { name: "Group D", value: 200 },
];

export default function AnalyticsDashboard() {
  const [carData, setCarData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [error, setError] = useState(null);
  const [sdkInitialized, setSdkInitialized] = useState(false);

  // Prepare data
  const carByStatus = carData.reduce((acc, car) => {
    const status = car.status || "Unknown";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const carByStatusData = Object.entries(carByStatus).map(([name, value]) => ({
    name,
    value,
  }));

  //

  // Prepare data
  const carByDate = carData.reduce((acc, car) => {
    const date = car.CAR_Date;
    if (date) {
      acc[date] = (acc[date] || 0) + 1;
    }
    return acc;
  }, {});
  const carByDateData = Object.entries(carByDate)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  //

  useEffect(() => {
    // First, initialize the SDK
    const initSDK = async () => {
      try {
        const initialized = await initializeZohoSDK();
        setSdkInitialized(initialized);

        if (initialized) {
          // Only try to get user info if SDK is initialized
          await logCurrentUserInfo();
        }
      } catch (err) {
        console.warn(
          "Could not initialize Zoho SDK, may be running in dev environment:",
          err
        );
      }
    };

    initSDK();
  }, []);

  useEffect(() => {
    // Only attempt to fetch data if SDK is initialized or we're in development mode
    if (!window.ZOHO || sdkInitialized) {
      // Fetch CAR records
      const fetchCarData = async () => {
        try {
          const records = await getCARRecords();
          setCarData(records || []);
        } catch (error) {
          console.error("Error fetching CAR data:", error);
          setError("Failed to load CAR records");
          setCarData([]);
        }
      };

      // Try to fetch data, but catch errors
      try {
        fetchCarData();
        // fetchAccounts();
      } catch (e) {
        console.error("Error in data fetching:", e);
        setError("An error occurred while loading data");
      }
    }
  }, [sdkInitialized]);

  // Group CARs by Machine for the pie chart
  const carByMachine = (() => {
    const map = {};
    carData.forEach((car) => {
      const machine =
        (car.Machine && (car.Machine.Name || car.Machine.zc_display_value)) ||
        "Unspecified";
      map[machine] = (map[machine] || 0) + 1;
    });
    // Convert to array for recharts
    return Object.entries(map)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  })();

  return (
    <Box px={3} py={3}>
      {/* Show error message if SDK initialization failed but we still need Zoho features */}
      {!sdkInitialized && window.location.hostname !== "localhost" && (
        <Box mb={2}></Box>
      )}
      <Grid container spacing={2}>
        {/* Card 1: Total CAR */}
        <Grid item md={6}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Total CAR
                </Typography>
                <Typography variant="button" color="#4F46E5">
                  Total CAR Records: {carData.length}
                </Typography>
              </Box>

              <Box
                height={180}
                width={300}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="h2" fontWeight="bold" color="#4F46E5">
                  {carData.length}
                </Typography>
              </Box>

              <Box mt={2}>
                <Divider />
                <Box display="flex" alignItems="center" mt={1} gap={1}>
                  <Typography variant="button">🕒</Typography>
                  <Typography variant="button" color="#4F46E5">
                    updated just now
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* Card 2: CAR by Machine Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  CAR by Machine
                </Typography>
                <Typography variant="button" color="#4F46E5">
                  Number of CARs per Machine
                </Typography>
              </Box>
              <Box height={180} width={300} sx={{ mx: "auto" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={carByMachine}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      fill="#4F46E5"
                      label={({ name, value }) => `${name} (${value})`}
                    >
                      {carByMachine.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={
                            [
                              "#4F46E5",
                              "#A020F0",
                              "#ffc658",
                              "#ff8042",
                              "#3ed6e7",
                            ][idx % 5]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        borderRadius: 8,
                        border: "1px solid #eee",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box mt={2}>
                <Divider />
                <Box display="flex" alignItems="center" mt={1} gap={1}>
                  <Typography variant="button">🕒</Typography>
                  <Typography variant="button" color="#4F46E5">
                    updated just now
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* Card 3: Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  CARs by Status
                </Typography>
                {/* <Typography variant="button" color="#4F46E5">
                  Last Campaign Performance
                </Typography> */}
              </Box>

              <Box height={180} width={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart width={400} height={300}>
                    <Pie
                      data={carByStatusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      label
                    />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box mt={2}>
                <Divider />
                <Box display="flex" alignItems="center" mt={1} gap={1}>
                  <Typography variant="button">🕒</Typography>
                  <Typography variant="button" color="#4F46E5">
                    just updated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* Card 4: Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Box mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  CARs Over Time
                </Typography>
                {/* <Typography variant="button" color="#4F46E5">
                  Last Campaign Performance
                </Typography> */}
              </Box>
              <Box height={180} width={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart width={600} height={300} data={carByDateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>

              <Box mt={2}>
                <Divider />
                <Box display="flex" alignItems="center" mt={1} gap={1}>
                  <Typography variant="button">🕒</Typography>
                  <Typography variant="button" color="#4F46E5">
                    just updated
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* Four different charts below card 4 */}
        <Grid item xs={12} md={3}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Line Chart
              </Typography>
              <Box height={180} width={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dummyLineData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#4F46E5"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Bar Chart
              </Typography>
              <Box height={180} width={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dummyBarData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#A020F0" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Pie Chart
              </Typography>
              <Box height={180} width={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dummyPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      fill="#ffc658"
                      label
                    >
                      {dummyPieData.map((entry, idx) => (
                        <Cell
                          key={`cell-pie-${idx}`}
                          fill={
                            ["#4F46E5", "#A020F0", "#ffc658", "#ff8042"][
                              idx % 4
                            ]
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={3} sx={{ minWidth: 350, minHeight: 340 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                Area Chart
              </Typography>
              <Box height={180} width={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dummyLineData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <defs>
                      <linearGradient
                        id="colorValue"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#4F46E5"
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor="#4F46E5"
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="#4F46E5"
                      fill="url(#colorValue)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
